import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  Clock,
  PersonCheck,
  GeoAlt,
  ChatLeftText,
  ArrowRight,
  CheckCircle,
  XCircle,
  Envelope,
  Telephone,
  CameraVideo,
  Building,
  Phone,
} from "react-bootstrap-icons";
import api from "../../services/api";

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { appointmentId, type, studentId } = location.state || {};

  const [bookingData, setBookingData] = useState({
    studentId: studentId || "",
    appointmentType: type || "zoom",
    scheduledDate: "",
    scheduledTime: "",
    counselor: "",
    location: type === "office" ? "" : "Phone Call",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [timeError, setTimeError] = useState("");

  const colors = {
    background: "#032530",
    text: "#FFFFFF",
    accent: "#ff8214",
    accentHover: "#ff9b44",
    border: "#1e273d",
    cardBg: "#0b2c36",
    progressBg: "#0b2c36",
    success: "#018744",
    warning: "#ffaa00",
    danger: "#ff4757",
  };

  // Office locations
  const officeLocations = [
    { value: "IEO Lahore Office", label: "IEO Lahore Office" },
    { value: "IEO Karachi Office", label: "IEO Karachi Office" },
    { value: "IEO Islamabad Office", label: "IEO Islamabad Office" },
    { value: "IEO Bangladesh Office", label: "IEO Bangladesh Office" },
  ];

  const counselors = [
    {
      id: "educational_advisor",
      name: "Educational Advisor",
      specialty: "Educational Advisor Expert",
      availability: "Mon-Sat",
    },
    {
      id: "visa_advisor",
      name: "Visa Advisor",
      specialty: "Visa Advisor Expert",
      availability: "Mon-Sat",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate location for office appointment
    if (bookingData.appointmentType === "office" && !bookingData.location) {
      setError("Please select an office location");
      setLoading(false);
      return;
    }

    // Validate time is within office hours
    const timeValidationError = validateTime(bookingData.scheduledTime);
    if (timeValidationError) {
      setTimeError(timeValidationError);
      setError(timeValidationError);
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/appointments", {
        studentId: bookingData.studentId,
        appointmentType: bookingData.appointmentType,
        scheduledDate: bookingData.scheduledDate,
        scheduledTime: bookingData.scheduledTime,
        counselor: bookingData.counselor,
        location: bookingData.location,
        notes: bookingData.notes,
        status: "pending",
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/");
        }, 3000);
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Booking failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const validateTime = (time) => {
    if (!time) return "";

    const [hours, minutes] = time.split(":").map(Number);
    const timeInMinutes = hours * 60 + minutes;
    const minTime = 11 * 60;
    const maxTime = 17 * 60;

    if (timeInMinutes < minTime || timeInMinutes > maxTime) {
      return "Please select a time between 11:00 AM and 5:00 PM (office hours)";
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setBookingData({
      ...bookingData,
      [name]: value,
    });

    // Validate time if it's the scheduledTime field
    if (name === "scheduledTime") {
      const error = validateTime(value);
      setTimeError(error);
    } else if (name !== "scheduledTime") {
      // Clear time error if other fields are changed
      setTimeError("");
    }
  };

  const handleAppointmentTypeChange = (typeValue) => {
    if (!location.state?.type) {
      setBookingData((prev) => ({
        ...prev,
        appointmentType: typeValue,
        location: typeValue === "office" ? "" : "Phone Call",
      }));
    }
  };

  const appointmentTypes = [
    {
      value: "zoom",
      label: "Call Back Request",
      icon: <Phone />,
      color: colors.accent, // Orange
    },
    {
      value: "office",
      label: "Office Consultation",
      icon: <Building />,
      color: colors.warning, // Yellow
    },
  ];

  return (
    <div
      style={{
        backgroundColor: colors.background,
        minHeight: "100vh",
        paddingTop: "2rem",
        paddingBottom: "2rem",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            {/* Header */}
            <div className="text-center mb-5">
              <h1
                style={{
                  color: colors.text,
                  fontWeight: "700",
                  marginBottom: "1rem",
                  fontSize: "2.5rem",
                }}
              >
                Book Your Consultation
              </h1>
              <p
                style={{
                  color: colors.text,
                  opacity: 0.8,
                  fontSize: "1.1rem",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Schedule your personalized{" "}
                {bookingData.appointmentType === "zoom"
                  ? "virtual"
                  : "in-person"}{" "}
                consultation
              </p>
            </div>

            <Card
              className="border-0"
              style={{
                backgroundColor: "#13181f",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: `0 10px 40px rgba(0, 0, 0, 0.3)`,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Card.Body style={{ padding: "2.5rem" }}>
                {error && (
                  <Alert
                    variant="danger"
                    onClose={() => setError(null)}
                    dismissible
                    style={{
                      backgroundColor: `${colors.danger}20`,
                      borderColor: colors.danger,
                      color: colors.text,
                      borderRadius: "10px",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <XCircle />
                      <strong>Error:</strong> {error}
                    </div>
                  </Alert>
                )}

                {success ? (
                  <div className="text-center py-4">
                    <div
                      style={{
                        color: colors.success,
                        fontSize: "4rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      <CheckCircle />
                    </div>
                    <h3
                      style={{
                        color: colors.text,
                        fontWeight: "700",
                        marginBottom: "1rem",
                      }}
                    >
                      Booking Confirmed!
                    </h3>
                    <p
                      style={{
                        color: colors.text,
                        opacity: 0.8,
                        marginBottom: "2rem",
                        fontSize: "1.1rem",
                      }}
                    >
                      Your consultation has been successfully scheduled.
                    </p>
                    <div
                      style={{
                        backgroundColor: `${colors.success}15`,
                        border: `2px solid ${colors.success}`,
                        borderRadius: "12px",
                        padding: "1.5rem",
                        maxWidth: "500px",
                        margin: "0 auto 2rem",
                      }}
                    >
                      <h5 style={{ color: colors.text, marginBottom: "1rem" }}>
                        Appointment Details
                      </h5>
                      <div className="text-start">
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ color: colors.text, opacity: 0.7 }}>
                            Date:
                          </span>
                          <span
                            style={{ color: colors.text, fontWeight: "500" }}
                          >
                            {new Date(
                              bookingData.scheduledDate,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ color: colors.text, opacity: 0.7 }}>
                            Time:
                          </span>
                          <span
                            style={{ color: colors.text, fontWeight: "500" }}
                          >
                            {bookingData.scheduledTime}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ color: colors.text, opacity: 0.7 }}>
                            Type:
                          </span>
                          <span
                            style={{ color: colors.text, fontWeight: "500" }}
                          >
                            {bookingData.appointmentType === "zoom"
                              ? "Zoom Session"
                              : "Office Consultation"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span style={{ color: colors.text, opacity: 0.7 }}>
                            Location:
                          </span>
                          <span
                            style={{ color: colors.text, fontWeight: "500" }}
                          >
                            {bookingData.location}
                          </span>
                        </div>
                        {bookingData.counselor && (
                          <div className="d-flex justify-content-between mb-2">
                            <span style={{ color: colors.text, opacity: 0.7 }}>
                              Counselor:
                            </span>
                            <span
                              style={{ color: colors.text, fontWeight: "500" }}
                            >
                              {
                                counselors.find(
                                  (c) => c.id === bookingData.counselor,
                                )?.name
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p
                      style={{
                        color: colors.text,
                        opacity: 0.6,
                        fontSize: "0.9rem",
                      }}
                    >
                      Your appointment has been booked successfully. Redirecting
                      to home page...
                    </p>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    {/* Appointment Type Selection */}
                    <div className="mb-5">
                      <h5
                        style={{
                          color: colors.text,
                          marginBottom: "1.5rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <CalendarCheck style={{ color: colors.accent }} />
                        Appointment Type
                      </h5>
                      <Row>
                        {appointmentTypes.map((type) => (
                          <Col md={6} key={type.value} className="mb-3">
                            <div
                              onClick={() =>
                                handleAppointmentTypeChange(type.value)
                              }
                              style={{
                                backgroundColor:
                                  bookingData.appointmentType === type.value
                                    ? `${type.color}15`
                                    : colors.background,
                                border: `2px solid ${
                                  bookingData.appointmentType === type.value
                                    ? type.color
                                    : colors.border
                                }`,
                                borderRadius: "12px",
                                padding: "1.25rem",
                                cursor: location.state?.type
                                  ? "not-allowed"
                                  : "pointer",
                                transition: "all 0.3s ease",
                                opacity:
                                  location.state?.type &&
                                  type.value !== location.state.type
                                    ? 0.5
                                    : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (
                                  !location.state?.type &&
                                  bookingData.appointmentType !== type.value
                                ) {
                                  e.currentTarget.style.backgroundColor = `${colors.accent}10`;
                                  e.currentTarget.style.borderColor = `${colors.accent}50`;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (
                                  !location.state?.type &&
                                  bookingData.appointmentType !== type.value
                                ) {
                                  e.currentTarget.style.backgroundColor =
                                    colors.background;
                                  e.currentTarget.style.borderColor =
                                    colors.border;
                                }
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    color: type.color,
                                    fontSize: "1.5rem",
                                    marginRight: "15px",
                                  }}
                                >
                                  {type.icon}
                                </div>
                                <div>
                                  <div
                                    style={{
                                      color: colors.text,
                                      fontWeight: "600",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    {type.label}
                                  </div>
                                  <div
                                    style={{
                                      color: colors.text,
                                      opacity: 0.7,
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    {type.value === "zoom"
                                      ? "Have questions? Fill out the form below and our team will call you back as soon as possible."
                                      : "Unlimited in-person consultation"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {/* Date & Time Selection */}
                    <Row className="mb-4">
                      <Col md={6} className="mb-4">
                        <h5
                          style={{
                            color: colors.text,
                            marginBottom: "1rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <CalendarCheck style={{ color: colors.accent }} />
                          Date & Time
                        </h5>
                        <Row>
                          <Col xs={12} className="mb-3">
                            <Form.Group>
                              <Form.Label
                                style={{
                                  color: colors.text,
                                  fontWeight: "500",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Preferred Date
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="scheduledDate"
                                value={bookingData.scheduledDate}
                                onChange={handleChange}
                                required
                                min={new Date().toISOString().split("T")[0]}
                                style={{
                                  backgroundColor: colors.background,
                                  border: `1px solid ${colors.border}`,
                                  color: colors.text,
                                  padding: "12px 14px",
                                  borderRadius: "10px",
                                  fontSize: "1rem",
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Label
                                style={{
                                  color: colors.text,
                                  fontWeight: "500",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Preferred Time
                              </Form.Label>
                              <Form.Control
                                type="time"
                                name="scheduledTime"
                                value={bookingData.scheduledTime}
                                onChange={handleChange}
                                required
                                min="11:00"
                                max="17:00"
                                isInvalid={!!timeError}
                                style={{
                                  backgroundColor: colors.background,
                                  border: `1px solid ${
                                    timeError ? colors.danger : colors.border
                                  }`,
                                  color: colors.text,
                                  padding: "12px 14px",
                                  borderRadius: "10px",
                                  fontSize: "1rem",
                                }}
                              />
                              {timeError && (
                                <Form.Control.Feedback
                                  type="invalid"
                                  style={{ display: "block" }}
                                >
                                  <small
                                    style={{
                                      color: colors.danger,
                                      marginTop: "4px",
                                    }}
                                  >
                                    {timeError}
                                  </small>
                                </Form.Control.Feedback>
                              )}
                              {!timeError && bookingData.scheduledTime && (
                                <Form.Text
                                  style={{
                                    color: colors.success,
                                    fontSize: "0.85rem",
                                    marginTop: "4px",
                                    display: "block",
                                  }}
                                >
                                  ✓ Valid time (Office hours: 11:00 AM - 5:00
                                  PM)
                                </Form.Text>
                              )}
                              {!timeError && !bookingData.scheduledTime && (
                                <Form.Text
                                  style={{
                                    color: colors.text,
                                    opacity: 0.6,
                                    fontSize: "0.85rem",
                                    marginTop: "4px",
                                    display: "block",
                                  }}
                                >
                                  Office hours: 11:00 AM - 5:00 PM
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Col>

                      <Col md={6} className="mb-4">
                        <h5
                          style={{
                            color: colors.text,
                            marginBottom: "1rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <PersonCheck style={{ color: colors.accent }} />
                          Select Counselor
                        </h5>
                        <div
                          style={{
                            backgroundColor: colors.background,
                            borderRadius: "10px",
                            padding: "1rem",
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          {counselors.map((counselor) => (
                            <div
                              key={counselor.id}
                              onClick={() =>
                                setBookingData((prev) => ({
                                  ...prev,
                                  counselor: counselor.id,
                                }))
                              }
                              style={{
                                backgroundColor:
                                  bookingData.counselor === counselor.id
                                    ? `${colors.accent}15`
                                    : "transparent",
                                border: `1px solid ${
                                  bookingData.counselor === counselor.id
                                    ? colors.accent
                                    : colors.border
                                }`,
                                borderRadius: "8px",
                                padding: "12px 15px",
                                marginBottom: "10px",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (bookingData.counselor !== counselor.id) {
                                  e.currentTarget.style.backgroundColor = `${colors.accent}10`;
                                  e.currentTarget.style.borderColor = `${colors.accent}50`;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (bookingData.counselor !== counselor.id) {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                  e.currentTarget.style.borderColor =
                                    colors.border;
                                }
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div
                                    style={{
                                      color: colors.text,
                                      fontWeight: "500",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    {counselor.name}
                                  </div>
                                  <div
                                    style={{
                                      color: colors.text,
                                      opacity: 0.7,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {counselor.specialty}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    color: colors.text,
                                    opacity: 0.6,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {counselor.availability}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>

                    {/* Location */}
                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group>
                          <h5
                            style={{
                              color: colors.text,
                              marginBottom: "1rem",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <GeoAlt style={{ color: colors.accent }} />
                            Location
                          </h5>
                          {bookingData.appointmentType === "zoom" ? (
                            <div
                              style={{
                                backgroundColor: colors.background,
                                border: `1px solid ${colors.border}`,
                                color: colors.text,
                                padding: "12px 14px",
                                borderRadius: "10px",
                                fontSize: "1rem",
                                opacity: 0.8,
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <CameraVideo
                                  size={18}
                                  style={{
                                    color: colors.accent,
                                    marginRight: "10px",
                                  }}
                                />
                                <span>Call Back Request</span>
                              </div>
                            </div>
                          ) : (
                            <Form.Control
                              as="select"
                              name="location"
                              value={bookingData.location}
                              onChange={handleChange}
                              required
                              style={{
                                backgroundColor: colors.background,
                                border: `1px solid ${colors.border}`,
                                color: colors.text,
                                padding: "12px 14px",
                                borderRadius: "10px",
                                fontSize: "1rem",
                              }}
                            >
                              <option value="">Select Office Location</option>
                              {officeLocations.map((office) => (
                                <option key={office.value} value={office.value}>
                                  {office.label}
                                </option>
                              ))}
                            </Form.Control>
                          )}
                          <div
                            style={{
                              color: colors.text,
                              opacity: 0.6,
                              fontSize: "0.85rem",
                              marginTop: "8px",
                              paddingLeft: "4px",
                            }}
                          >
                            {bookingData.appointmentType === "zoom"
                              ? "Enter your details and our team will contact you shortly"
                              : "Please select your preferred office location"}
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <h5
                            style={{
                              color: colors.text,
                              marginBottom: "1rem",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <ChatLeftText style={{ color: colors.accent }} />
                            Additional Notes
                          </h5>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="notes"
                            value={bookingData.notes}
                            onChange={handleChange}
                            style={{
                              backgroundColor: colors.background,
                              border: `1px solid ${colors.border}`,
                              color: colors.text,
                              padding: "12px 14px",
                              borderRadius: "10px",
                              fontSize: "1rem",
                              resize: "vertical",
                            }}
                            placeholder="Any specific questions, concerns, or topics you'd like to discuss..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: colors.accent,
                        borderColor: colors.accent,
                        color: colors.background,
                        borderRadius: "12px",
                        padding: "16px",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        width: "100%",
                        transition: "all 0.3s ease",
                        boxShadow: `0 5px 20px rgba(255, 130, 20, 0.3)`,
                        marginTop: "1rem",
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.currentTarget.style.backgroundColor =
                            colors.accentHover;
                          e.currentTarget.style.borderColor =
                            colors.accentHover;
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.boxShadow = `0 8px 25px rgba(255, 155, 68, 0.4)`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.currentTarget.style.backgroundColor = colors.accent;
                          e.currentTarget.style.borderColor = colors.accent;
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = `0 5px 20px rgba(255, 130, 20, 0.3)`;
                        }
                      }}
                    >
                      {loading ? (
                        <>Processing...</>
                      ) : (
                        <>
                          Confirm Booking
                          <ArrowRight size={20} className="ms-2" />
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </Card.Body>
            </Card>

            {/* Additional Information */}
            {!success && (
              <div
                style={{
                  marginTop: "2rem",
                  padding: "1.5rem",
                  backgroundColor: colors.cardBg,
                  borderRadius: "12px",
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Row>
                  <Col md={4} className="text-center mb-3">
                    <div className="d-flex flex-column align-items-center">
                      <Envelope
                        style={{
                          color: colors.accent,
                          marginBottom: "10px",
                          fontSize: "1.5rem",
                        }}
                      />
                      <div style={{ color: colors.text, fontWeight: "500" }}>
                        Booking Confirmation
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          opacity: 0.7,
                          fontSize: "0.85rem",
                        }}
                      >
                        Sent within 5 minutes
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-center mb-3">
                    <div className="d-flex flex-column align-items-center">
                      <Clock
                        style={{
                          color: colors.accent,
                          marginBottom: "10px",
                          fontSize: "1.5rem",
                        }}
                      />
                      <div style={{ color: colors.text, fontWeight: "500" }}>
                        Duration
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          opacity: 0.7,
                          fontSize: "0.85rem",
                        }}
                      >
                        {bookingData.appointmentType === "zoom"
                          ? "30 minutes"
                          : "45 minutes"}
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-center mb-3">
                    <div className="d-flex flex-column align-items-center">
                      <Telephone
                        style={{
                          color: colors.accent,
                          marginBottom: "10px",
                          fontSize: "1.5rem",
                        }}
                      />
                      <div style={{ color: colors.text, fontWeight: "500" }}>
                        Need Help?
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          opacity: 0.7,
                          fontSize: "0.85rem",
                        }}
                      >
                        +353 89 941 7647
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Custom Styles */}
      <style jsx="true">{`
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: ${colors.background};
        }

        ::-webkit-scrollbar-thumb {
          background: ${colors.accent};
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.accentHover};
        }

        .form-control:focus {
          background-color: ${colors.background};
          border-color: ${colors.accent};
          color: ${colors.text};
          box-shadow: 0 0 0 0.25rem rgba(255, 130, 20, 0.25);
        }

        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default BookingPage;
